/* eslint-disable react-refresh/only-export-components */
import { Suspense, useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import {
  AuthLayout,
  DashboardLayout,
  CompanyManagementLayout,
} from '@/components/layout';
import { lazyPage } from '@/lib/lazy-page';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleBasedRedirect } from './RoleBasedRedirect';
import { RouteErrorPage } from './RouteErrorPage';
import type { StaffRole } from '@/types/auth.types';
import { useAuth } from '@/contexts';
import { companyService } from '@/services/company.service';
import { isAbortError } from '@/hooks/useAbortableEffect';

// ============================================================================
// LAZY PAGE IMPORTS — code-split each page into its own chunk
// ============================================================================

const LoginPage = lazyPage(() => import('@/pages/auth/LoginPage'));
const TotpChallengePage = lazyPage(
  () => import('@/pages/auth/TotpChallengePage')
);
const RecoveryCodePage = lazyPage(
  () => import('@/pages/auth/RecoveryCodePage')
);
const SecuritySettingsPage = lazyPage(
  () => import('@/pages/settings/SecuritySettingsPage')
);
const NotFoundPage = lazyPage(() => import('@/pages/NotFoundPage'));
const CountryConfigPage = lazyPage(
  () => import('@/pages/country-config/CountryConfigPage')
);
const CurrenciesPage = lazyPage(
  () => import('@/pages/currencies/CurrenciesPage')
);
const LanguagesPage = lazyPage(() => import('@/pages/languages/LanguagesPage'));
const ProvincesPage = lazyPage(() => import('@/pages/provinces/ProvincesPage'));
const PaymentGatewaysPage = lazyPage(
  () => import('@/pages/payment-gateways/PaymentGatewaysPage')
);
const StaffPage = lazyPage(() => import('@/pages/staff/StaffPage'));
const MonitoringPage = lazyPage(
  () => import('@/pages/monitoring/MonitoringPage')
);
const ServerMonitoringPage = lazyPage(
  () => import('@/pages/monitoring/ServerMonitoringPage')
);
const SupportMonitoringPage = lazyPage(
  () => import('@/pages/monitoring/SupportMonitoringPage')
);
const CompaniesPage = lazyPage(() => import('@/pages/companies/CompaniesPage'));
const CompanyDetailPage = lazyPage(() =>
  import('@/pages/companies/CompanyDetailPage').then((m) => ({
    default: m.CompanyDetailPage,
  }))
);
const StoreDetailPage = lazyPage(() =>
  import('@/pages/companies/StoreDetailPage').then((m) => ({
    default: m.StoreDetailPage,
  }))
);
const MyCompaniesPage = lazyPage(
  () => import('@/pages/companies/MyCompaniesPage')
);
const SupportCompaniesPage = lazyPage(
  () => import('@/pages/companies/SupportCompaniesPage')
);
const CompanyStoresPage = lazyPage(
  () => import('@/pages/companies/CompanyStoresPage')
);
const ContentCreatorsPage = lazyPage(
  () => import('@/pages/content-creators/ContentCreatorsPage')
);
const CreatorCampaignsPage = lazyPage(
  () => import('@/pages/content-creators/CreatorCampaignsPage')
);
const TaxesPage = lazyPage(() => import('@/pages/taxes/TaxesPage'));
const ServiceFeesPage = lazyPage(
  () => import('@/pages/service-fees/ServiceFeesPage')
);
const OrganizationsPage = lazyPage(
  () => import('@/pages/organizations/OrganizationsPage')
);
const EventStatsPage = lazyPage(
  () => import('@/pages/organizations/EventStatsPage')
);
const PlatformSettingsPage = lazyPage(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({
    default: m.PlatformSettingsPage,
  }))
);
const SupportTicketsPage = lazyPage(
  () => import('@/pages/support-tickets/SupportTicketsPage')
);
const MerchantLeadsPage = lazyPage(
  () => import('@/pages/merchant-leads/MerchantLeadsPage')
);
const AdvisorLeadsPage = lazyPage(
  () => import('@/pages/advisor-leads/AdvisorLeadsPage')
);
const MerchantRecommendationsPage = lazyPage(
  () => import('@/pages/merchant-recommendations/MerchantRecommendationsPage')
);
const NotificationsPage = lazyPage(
  () => import('@/pages/notifications/NotificationsPage')
);
const SupportAgentsPage = lazyPage(
  () => import('@/pages/support-agents/SupportAgentsPage')
);
const RefundRequestsPage = lazyPage(
  () => import('@/pages/refund-requests/RefundRequestsPage')
);
const DocumentacionPage = lazyPage(
  () => import('@/pages/documentacion/DocumentacionPage')
);
const CustomersPage = lazyPage(() => import('@/pages/customers/CustomersPage'));
const CustomerDetailPage = lazyPage(
  () => import('@/pages/customers/CustomerDetailPage')
);
const ReportsPage = lazyPage(() => import('@/pages/reports/ReportsPage'));
const MyReportsPage = lazyPage(() => import('@/pages/reports/MyReportsPage'));
const AdvertisingPage = lazyPage(
  () => import('@/pages/advertising/AdvertisingPage')
);
const InAppNoticesPage = lazyPage(
  () => import('@/pages/in-app-notices/InAppNoticesPage')
);
const StatisticsPage = lazyPage(
  () => import('@/pages/statistics/StatisticsPage')
);
const GiftsPage = lazyPage(() => import('@/pages/gifts/GiftsPage'));
const PromoCampaignDetailPage = lazyPage(
  () => import('@/pages/campaigns/CampaignDetailPage')
);
const PointsCampaignDetailPage = lazyPage(
  () => import('@/pages/points-flow/PointsCampaignDetailPage')
);
const ReferralReportPage = lazyPage(() =>
  import('@/pages/referrals/ReferralReportPage').then((m) => ({
    default: m.ReferralReportPage,
  }))
);
const PointsCashbackPage = lazyPage(
  () => import('@/pages/points-cashback/PointsCashbackPage')
);
const FinanceDashboardPage = lazyPage(
  () => import('@/pages/finance/FinanceDashboardPage')
);
const FinanceCompaniesPage = lazyPage(
  () => import('@/pages/finance/FinanceCompaniesPage')
);
const FinanceCreatorsPage = lazyPage(
  () => import('@/pages/finance/FinanceCreatorsPage')
);
const FinancePaymentsPage = lazyPage(
  () => import('@/pages/finance/FinancePaymentsPage')
);
const FinanceStorePayoutsPage = lazyPage(
  () => import('@/pages/finance/FinanceStorePayoutsPage')
);
const ScheduledPayoutsPage = lazyPage(
  () => import('@/pages/finance/scheduled-payouts/ScheduledPayoutsPage')
);
const FinanceBankAccountsPage = lazyPage(
  () => import('@/pages/finance/FinanceBankAccountsPage')
);
const FinanceInfluencerPayoutsPage = lazyPage(
  () => import('@/pages/finance/FinanceInfluencerPayoutsPage')
);
const FinanceSubscriptionsPage = lazyPage(
  () => import('@/pages/finance/FinanceSubscriptionsPage')
);
const FinanceWalletWithdrawalsPage = lazyPage(
  () => import('@/pages/finance/FinanceWalletWithdrawalsPage')
);
const FinanceWalletsPage = lazyPage(
  () => import('@/pages/finance/FinanceWalletsPage')
);
const FinanceReportsPage = lazyPage(
  () => import('@/pages/finance/FinanceReportsPage')
);
const AppVersionsPage = lazyPage(
  () => import('@/pages/app-versions/AppVersionsPage')
);
const CommercialAdvisorsPage = lazyPage(
  () => import('@/pages/commercial-advisors/CommercialAdvisorsPage')
);
const MyAdvisorCommissionsPage = lazyPage(
  () => import('@/pages/my-advisor/MyAdvisorCommissionsPage')
);
const FinanceAdvisorPayoutsPage = lazyPage(
  () => import('@/pages/finance/FinanceAdvisorPayoutsPage')
);
const MyAdvisorPayoutsPage = lazyPage(
  () => import('@/pages/my-advisor/MyAdvisorPayoutsPage')
);

// Store Management (advisor proxy)
const StoreMenuPage = lazyPage(
  () => import('@/pages/store-management/StoreMenuPage')
);
const CatalogPage = lazyPage(
  () => import('@/pages/store-management/CatalogPage')
);
const StoreSchedulePage = lazyPage(
  () => import('@/pages/store-management/StoreSchedulePage')
);
const StoreSettingsPage = lazyPage(
  () => import('@/pages/store-management/StoreSettingsPage')
);
const StorePromotionsPage = lazyPage(
  () => import('@/pages/store-management/StorePromotionsPage')
);
const StoreLoyaltyPage = lazyPage(
  () => import('@/pages/store-management/StoreLoyaltyPage')
);
const CreateStorePage = lazyPage(
  () => import('@/pages/store-management/CreateStorePage')
);

// ============================================================================
// ROUTE LOADING FALLBACK
// ============================================================================

function RouteLoading() {
  return (
    <div
      className="animate-pulse space-y-6"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-6 w-48 rounded-md bg-background-tertiary" />
          <div className="h-3 w-64 rounded bg-background-secondary" />
        </div>
        <div className="flex gap-2">
          <div className="h-9 w-24 rounded-lg bg-background-secondary" />
          <div className="h-9 w-32 rounded-lg bg-background-tertiary" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="h-24 rounded-xl bg-background-secondary" />
        <div className="h-24 rounded-xl bg-background-secondary" />
        <div className="h-24 rounded-xl bg-background-secondary" />
        <div className="h-24 rounded-xl bg-background-secondary" />
      </div>

      <div className="rounded-xl bg-background-secondary p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-4 w-40 rounded bg-background-tertiary" />
          <div className="h-8 w-24 rounded-md bg-background-tertiary" />
        </div>
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-background-tertiary" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-[55%] rounded bg-background-tertiary" />
                <div className="h-2.5 w-[35%] rounded bg-background-tertiary/70" />
              </div>
              <div className="h-8 w-20 rounded-md bg-background-tertiary" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Helper — wraps a lazy component in Suspense with the route loading spinner */
function withSuspense(Component: React.ReactNode) {
  return <Suspense fallback={<RouteLoading />}>{Component}</Suspense>;
}

/**
 * Suspense fallback for the auth pages (login / TOTP / recovery). Mirrors the
 * centered auth Card — heading, subtitle, one input, primary + secondary action —
 * instead of the dashboard-shaped RouteLoading, so there's no jarring layout flash
 * before the login screen mounts.
 */
function AuthRouteLoading() {
  return (
    <div
      className="animate-pulse rounded-xl bg-background-secondary p-6 sm:p-8"
      aria-busy="true"
      aria-live="polite"
    >
      {/* Heading + subtitle */}
      <div className="mb-6 space-y-3">
        <div className="mx-auto h-6 w-44 rounded-md bg-background-tertiary" />
        <div className="mx-auto h-3 w-60 rounded bg-background-tertiary" />
      </div>

      {/* Input field */}
      <div className="mb-4 h-11 w-full rounded-lg bg-background-tertiary" />

      {/* Primary action + secondary link */}
      <div className="space-y-3">
        <div className="h-11 w-full rounded-lg bg-background-tertiary" />
        <div className="mx-auto h-3 w-28 rounded bg-background-tertiary" />
      </div>
    </div>
  );
}

/** Helper — wraps a lazy auth page in Suspense with the centered-card fallback */
function withAuthSuspense(Component: React.ReactNode) {
  return <Suspense fallback={<AuthRouteLoading />}>{Component}</Suspense>;
}

// ============================================================================
// ROLE-AWARE ROUTE WRAPPERS
// ============================================================================

/**
 * Route-level role access control constants.
 * Maps each route group to the backend roles that have permission to access
 * the APIs and functionality within those pages.
 *
 * IMPORTANT: These MUST match the backend @Roles() decorators.
 * When adding new routes, always verify backend permissions first.
 */
/** `/campaigns/:id` (antes de «Regalos») → detalle de la campaña de bono. */
function LegacyCampaignRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={`/gifts/bonos/campanas/${id}`} replace />;
}

const ROLES = {
  /** Platform-level config: currencies, languages, settings, server monitoring, staff */
  PLATFORM_ADMIN: ['SUPER_ADMIN'] as StaffRole[],
  /** Country config: provinces, cities, payment gateways */
  COUNTRY_ADMIN: ['SUPER_ADMIN'] as StaffRole[],
  /** Taxes — SUPER_ADMIN + FINANCE (backend grants FINANCE access) */
  TAXES: ['SUPER_ADMIN', 'FINANCE'] as StaffRole[],
  /** Service Fees — SUPER_ADMIN + FINANCE (backend grants FINANCE access) */
  SERVICE_FEES: ['SUPER_ADMIN', 'FINANCE'] as StaffRole[],
  /** Companies listing (full catalog) — SUPER_ADMIN only.
   *  FINANCE uses /finance-companies for financial view. Asesores entran por
   *  /my-companies. */
  COMPANIES: ['SUPER_ADMIN'] as StaffRole[],
  /** Detalle/edición de una compañía individual.
   *  Asesores (SALES_MANAGER, SUPPORT_AGENT) pueden abrir el detalle de las
   *  compañías que tienen asignadas — la página fuerza readOnly según el rol
   *  (`company-detail-access.ts`), y el backend verifica que el asesor esté
   *  efectivamente vinculado a esa company antes de servirla.
   *
   *  SUPPORT_LEAD entra por `/support-companies`, cuya única acción navega
   *  aquí para gestionar los asesores de la compañía. Faltaba en esta lista,
   *  así que esa pantalla expulsaba al rol para el que fue escrita (cap. 40). */
  COMPANY_DETAIL: [
    'SUPER_ADMIN',
    'SUPPORT_LEAD',
    'SALES_MANAGER',
    'SUPPORT_AGENT',
    'COMMERCIAL_ADVISOR',
  ] as StaffRole[],
  /** My advised companies (advisor view) */
  MY_COMPANIES: [
    'SUPER_ADMIN',
    'SALES_MANAGER',
    'SUPPORT_AGENT',
    'COMMERCIAL_ADVISOR',
  ] as StaffRole[],
  /** Content creators / influencers — FINANCE uses /finance-creators for payouts view */
  CONTENT_CREATORS: [
    'SUPER_ADMIN',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
    'MARKETING',
  ] as StaffRole[],
  /** Organizations management — SUPER_ADMIN/MARKETING ven todo el país,
   *  asesores entran a la misma vista (sin scope automático en el front)
   *  porque las mutaciones store↔org las hacen desde sus sucursales en
   *  /my-companies. */
  ORGANIZATIONS: [
    'SUPER_ADMIN',
    'MARKETING',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
    'SALES_MANAGER',
  ] as StaffRole[],
  /** Monitoring (operational) — SUPER_ADMIN gets full, SUPPORT_LEAD gets support-only */
  MONITORING: ['SUPER_ADMIN', 'SUPPORT_LEAD'] as StaffRole[],
  /** Support tickets — all support roles */
  SUPPORT_TICKETS: [
    'SUPER_ADMIN',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
  ] as StaffRole[],
  /** Solicitudes de integración (lead capture del landing) — gestionadas por soporte */
  MERCHANT_LEADS: [
    'SUPER_ADMIN',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
  ] as StaffRole[],
  /**
   * Comercios recomendados por usuarios (Bono Pionero). Misma puerta que las
   * solicitudes: para el agente es el mismo buzón comercial. Quién puede
   * convertir o liberar un bono lo decide el backend, no esta lista.
   */
  MERCHANT_RECOMMENDATIONS: [
    'SUPER_ADMIN',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
  ] as StaffRole[],
  /**
   * Solicitudes de reembolso. El agente entra para ver las suyas y retirarlas;
   * los revisores para aprobar y rechazar. Quién puede hacer qué lo decide el
   * backend — esta lista sólo abre la puerta.
   */
  REFUND_REQUESTS: [
    'SUPER_ADMIN',
    'FINANCE',
    'SUPPORT_LEAD',
    'SUPPORT_AGENT',
  ] as StaffRole[],
  /** Support agents management — lead level */
  SUPPORT_MANAGEMENT: ['SUPER_ADMIN', 'SUPPORT_LEAD'] as StaffRole[],
  /** Reports — support leads only */
  SUPPORT_REPORTS: ['SUPER_ADMIN', 'SUPPORT_LEAD'] as StaffRole[],
  /** Staff management */
  STAFF: ['SUPER_ADMIN', 'SUPPORT_LEAD'] as StaffRole[],
  /** Customers (app users) — SUPER_ADMIN ve todo (incluye billetera), SUPPORT_LEAD
   *  ve perfil + historial de órdenes. La billetera la bloquea el backend. */
  CUSTOMERS: ['SUPER_ADMIN', 'SUPPORT_LEAD'] as StaffRole[],
  /** Marketing-only features: advertising, statistics, promo credits */
  MARKETING: ['SUPER_ADMIN', 'MARKETING'] as StaffRole[],
  /** Finance module: dashboards, payments, payouts, commissions, invoices, wallets, reports */
  FINANCE: ['SUPER_ADMIN', 'FINANCE'] as StaffRole[],
  /**
   * Bank accounts review — los asesores asignados a una company
   * pueden aprobar/rechazar sus cuentas; SUPER_ADMIN/FINANCE tienen
   * visión global. El backend filtra por CompanyAdvisor cuando el
   * staff no es SUPER_ADMIN/FINANCE.
   */
  BANK_ACCOUNT_REVIEW: [
    'SUPER_ADMIN',
    'FINANCE',
    'SUPPORT_LEAD',
    'SALES_MANAGER',
    'SUPPORT_AGENT',
  ] as StaffRole[],
  /** Store management: advisors managing stores on behalf of companies */
  STORE_MANAGEMENT: [
    'SUPER_ADMIN',
    'SALES_MANAGER',
    'SUPPORT_AGENT',
    'SUPPORT_LEAD',
  ] as StaffRole[],
  /** Asesores comerciales (admin) — SUPER_ADMIN/FINANCE escritura,
   *  SUPPORT_LEAD lectura (el backend restringe el PATCH). */
  COMMERCIAL_ADVISORS_ADMIN: [
    'SUPER_ADMIN',
    'FINANCE',
    'SUPPORT_LEAD',
  ] as StaffRole[],
} as const;

/**
 * Renders MonitoringPage (full) for SUPER_ADMIN,
 * or SupportMonitoringPage (tickets only) for SUPPORT_LEAD.
 */
function MonitoringRoute() {
  const { user } = useAuth();
  const role = user?.profiles?.staff?.role;

  if (role === 'SUPPORT_LEAD') {
    return withSuspense(<SupportMonitoringPage />);
  }
  return withSuspense(<MonitoringPage />);
}

/**
 * Redirects to Company Mode by auto-selecting the first store.
 * Route: /my-companies/:companyId/manage
 * → /my-companies/:companyId/stores/:firstStoreId/manage/catalog
 */
function CompanyModeRedirect() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;

    // Sin cancelación, quien pulsa «Gestionar» y cambia de idea antes de que
    // resuelva la consulta acaba arrastrado al catálogo desde la pantalla a la
    // que había navegado.
    const ctrl = new AbortController();

    companyService
      .getStores(companyId, { limit: 1 }, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        if (res.data.length > 0) {
          navigate(
            `/my-companies/${companyId}/stores/${res.data[0].id}/manage/catalog`,
            { replace: true }
          );
        } else {
          navigate(`/my-companies/${companyId}/stores/create`, {
            replace: true,
          });
        }
      })
      .catch((err) => {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        // El servidor explica si la compañía no está en la cartera; antes se
        // descartaba el mensaje y salía siempre el mismo texto.
        setError(
          err instanceof Error ? err.message : 'Error al cargar sucursales'
        );
      });

    return () => ctrl.abort();
  }, [companyId, navigate]);

  if (error) {
    return (
      <div className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">{error}</p>
        <button
          onClick={() => navigate('/my-companies')}
          className="text-sm text-primary hover:underline cursor-pointer"
        >
          Volver
        </button>
      </div>
    );
  }

  return <RouteLoading />;
}

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================

/**
 * Professional router structure with:
 * - Public routes (auth)
 * - Protected routes (dashboard)
 * - Automatic redirections
 * - 404 handling
 */
/**
 * Tabla de rutas de este panel.
 *
 * Se exporta aparte de `createBrowserRouter` porque además de enrutar sirve
 * para **preguntarle a la aplicación si una ruta existe aquí**. El
 * `deepLink` de las notificaciones vive en una columna compartida por la
 * app móvil y los tres frontales, sin nada que diga a cuál pertenece cada
 * ruta; sin consultar esta tabla el panel navegaba a rutas ajenas y
 * aterrizaba en su propio 404. Ver `resolveDashboardLink`.
 */
export const routes: RouteObject[] = [
  {
    // Ruta raíz sin path: aporta a todas las rutas hijas el errorElement global
    // (auto-reload de chunks viejos tras un deploy + pantalla amable de error).
    errorElement: <RouteErrorPage />,
    children: [
      // Root redirect — sends each role to their first navigation item
      {
        path: '/',
        element: (
          <ProtectedRoute requireAuth={true}>
            <RoleBasedRedirect />
          </ProtectedRoute>
        ),
      },

      // =========================================================================
      // PUBLIC ROUTES - Authentication
      // =========================================================================
      {
        path: '/auth',
        element: (
          <ProtectedRoute requireAuth={false}>
            <AuthLayout />
          </ProtectedRoute>
        ),
        children: [
          // Redirect /auth to /auth/login
          {
            index: true,
            element: <Navigate to="/auth/login" replace />,
          },
          {
            path: 'login',
            element: withAuthSuspense(<LoginPage />),
          },
          {
            path: 'totp-challenge',
            element: withAuthSuspense(<TotpChallengePage />),
          },
          {
            path: 'recovery-code',
            element: withAuthSuspense(<RecoveryCodePage />),
          },
          {
            path: 'forgot-password',
            element: <Navigate to="/auth/login" replace />,
          },
        ],
      },

      // =========================================================================
      // PROTECTED ROUTES - Dashboard Layout
      // =========================================================================

      // Account security (TOTP + trusted devices). Available to EVERY authenticated
      // staff regardless of role — TOTP enforcement for high-privilege roles
      // (SUPER_ADMIN / FINANCE) is handled at login by redirecting here with
      // ?setupTotp=1.
      {
        path: '/account/security',
        element: (
          <ProtectedRoute requireAuth={true}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<SecuritySettingsPage />),
          },
        ],
      },

      // SUPER_ADMIN Routes

      // Country Configuration Hub
      {
        path: '/country-config',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.COUNTRY_ADMIN}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CountryConfigPage />),
          },
        ],
      },

      {
        path: '/currencies',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.PLATFORM_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CurrenciesPage />),
          },
        ],
      },

      // Languages route
      {
        path: '/languages',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.PLATFORM_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<LanguagesPage />),
          },
        ],
      },

      // App Versions route
      {
        path: '/app-versions',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.PLATFORM_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<AppVersionsPage />),
          },
        ],
      },

      // Provinces route (nested under country)
      {
        path: '/countries/:countryId/provinces',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.COUNTRY_ADMIN}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ProvincesPage />),
          },
        ],
      },

      // Payment Gateways route
      {
        path: '/payment-gateways',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.COUNTRY_ADMIN}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<PaymentGatewaysPage />),
          },
        ],
      },

      // Staff (Colaboradores) route
      {
        path: '/staff',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.STAFF}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<StaffPage />),
          },
        ],
      },

      // Monitoring route (country-scoped — role-aware)
      {
        path: '/monitoring',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MONITORING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <MonitoringRoute />,
          },
        ],
      },

      // Server monitoring route (platform-level infrastructure)
      {
        path: '/server-monitoring',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.PLATFORM_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ServerMonitoringPage />),
          },
        ],
      },

      // My Companies route (advisor view for SALES_MANAGER)
      {
        path: '/my-companies',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MY_COMPANIES}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MyCompaniesPage />),
          },
        ],
      },

      // Companies route
      {
        path: '/companies',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.COMPANIES}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CompaniesPage />),
          },
        ],
      },

      // Company detail page (dedicated route — replaces modal)
      // Abierto a asesores (readOnly via CompanyDetailPage); backend valida que
      // el asesor tenga la company asignada antes de servir el endpoint.
      {
        path: '/companies/:companyId',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.COMPANY_DETAIL}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CompanyDetailPage />),
          },
        ],
      },

      // Company Stores list
      // Asesores entran via /my-companies/:companyId/stores; este path queda
      // disponible para mantener consistencia interna del CompanyDetailPage,
      // que linkea a /companies/:companyId/stores. Backend valida acceso.
      {
        path: '/companies/:companyId/stores',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.COMPANY_DETAIL}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CompanyStoresPage />),
          },
        ],
      },

      // Company Stores — Create Store.
      //
      // Sólo existía bajo `/my-companies`, así que el botón «+ Crear Sucursal» se
      // ocultaba en la rama `/companies` —la que usa el SUPER_ADMIN desde el
      // detalle— y ese rol se quedaba sin poder crear sucursales desde el panel,
      // pese a que el backend se lo permite en cualquier compañía.
      {
        path: '/companies/:companyId/stores/create',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.COMPANY_DETAIL}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CreateStorePage />),
          },
        ],
      },

      // Store detail page (dedicated route — replaces modal)
      {
        path: '/companies/:companyId/stores/:storeId',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.COMPANY_DETAIL}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<StoreDetailPage />),
          },
        ],
      },

      // Support Companies route (SUPPORT_LEAD — manage advisors)
      {
        path: '/support-companies',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.SUPPORT_MANAGEMENT}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<SupportCompaniesPage />),
          },
        ],
      },

      // Content Creators (Influencers) route
      {
        path: '/content-creators',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={[...ROLES.CONTENT_CREATORS, 'FINANCE']}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ContentCreatorsPage />),
          },
          // Las campañas viven dentro de Creadores: una campaña no existe sin
          // creadores que la repartan.
          {
            path: 'campaigns',
            element: withSuspense(<CreatorCampaignsPage />),
          },
        ],
      },

      // «Pagos a asesores» (admin): asesores y comisiones + sus liquidaciones.
      // Hasta el 2026-10-15 las liquidaciones eran otra entrada del menú
      // (`/finance-advisor-payouts`, que ahora redirige aquí).
      {
        path: '/commercial-advisors',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.COMMERCIAL_ADVISORS_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CommercialAdvisorsPage />),
          },
          // SUPPORT_LEAD ve la lista en lectura, pero no las liquidaciones.
          {
            path: 'payouts',
            element: (
              <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
                {withSuspense(<FinanceAdvisorPayoutsPage />)}
              </ProtectedRoute>
            ),
          },
        ],
      },

      // Mis Comisiones (asesor comercial autenticado)
      {
        path: '/my-advisor-commissions',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={['COMMERCIAL_ADVISOR'] as StaffRole[]}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MyAdvisorCommissionsPage />),
          },
        ],
      },

      // Asesores · Pagos — se unió a «Pagos a asesores» el 2026-10-15.
      {
        path: '/finance-advisor-payouts',
        element: <Navigate to="/commercial-advisors/payouts" replace />,
      },

      // Mis Pagos (asesor comercial autenticado)
      {
        path: '/my-advisor-payouts',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={['COMMERCIAL_ADVISOR'] as StaffRole[]}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MyAdvisorPayoutsPage />),
          },
        ],
      },

      // Taxes route
      {
        path: '/taxes',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.TAXES}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<TaxesPage />),
          },
        ],
      },

      // Service Fees route
      {
        path: '/service-fees',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.SERVICE_FEES}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ServiceFeesPage />),
          },
        ],
      },

      // Organizations route
      {
        path: '/organizations',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.ORGANIZATIONS}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<OrganizationsPage />),
          },
        ],
      },

      // Event statistics (Marketing / SUPER_ADMIN)
      {
        path: '/organizations/:id/event-stats',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<EventStatsPage />),
          },
        ],
      },

      // Settings (Platform Config) route
      {
        path: '/settings',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.PLATFORM_ADMIN}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<PlatformSettingsPage />),
          },
        ],
      },

      // Support Tickets route (global, all countries)
      {
        path: '/support-tickets',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.SUPPORT_TICKETS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<SupportTicketsPage />),
          },
        ],
      },

      // Merchant Leads — solicitudes de integración del landing
      {
        path: '/leads',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.MERCHANT_LEADS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MerchantLeadsPage />),
          },
        ],
      },

      // Bono Pionero — comercios que recomiendan los usuarios desde la app
      {
        path: '/recommendations',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.MERCHANT_RECOMMENDATIONS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MerchantRecommendationsPage />),
          },
        ],
      },

      // Advisor Leads — solicitudes de asesores comerciales del landing
      {
        path: '/advisor-leads',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.MERCHANT_LEADS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<AdvisorLeadsPage />),
          },
        ],
      },

      // Notifications inbox — any authenticated staff can have their own
      {
        path: '/notifications',
        element: (
          <ProtectedRoute requireAuth={true}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<NotificationsPage />),
          },
        ],
      },

      // Documentación — el manual, con asistente. Cualquier colaborador entra:
      // el acotado no es por quién entra sino por qué ve cada uno, y eso lo
      // resuelve el servidor cruzando cada capítulo con su menú.
      {
        path: '/documentacion',
        element: (
          <ProtectedRoute requireAuth={true}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<DocumentacionPage />),
          },
        ],
      },

      // Solicitudes de reembolso — el agente pide, el líder decide
      {
        path: '/refund-requests',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.REFUND_REQUESTS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<RefundRequestsPage />),
          },
        ],
      },

      // Support Agents route (SUPPORT_LEAD management)
      {
        path: '/support-agents',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.SUPPORT_MANAGEMENT}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<SupportAgentsPage />),
          },
        ],
      },

      // Customers routes (SUPER_ADMIN full + wallet, SUPPORT_LEAD profile + orders)
      {
        path: '/customers',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.CUSTOMERS}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CustomersPage />),
          },
          {
            path: ':id',
            element: withSuspense(<CustomerDetailPage />),
          },
        ],
      },

      // Reports route (SUPPORT_LEAD / SUPER_ADMIN)
      {
        path: '/reports',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.SUPPORT_REPORTS}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ReportsPage />),
          },
        ],
      },

      // My Reports route (SUPPORT_AGENT — personal reports)
      {
        path: '/my-reports',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={['SUPPORT_AGENT'] as StaffRole[]}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<MyReportsPage />),
          },
        ],
      },

      // =========================================================================
      // FINANCE ROUTES
      // =========================================================================

      // Finance Dashboard
      {
        path: '/finance-dashboard',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceDashboardPage />),
          },
        ],
      },

      // Finance Companies — revenue view
      {
        path: '/finance-companies',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceCompaniesPage />),
          },
        ],
      },

      // Finance Content Creators — earnings view
      {
        path: '/finance-creators',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceCreatorsPage />),
          },
        ],
      },

      // Finance Payments & Transactions
      {
        path: '/finance-payments',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinancePaymentsPage />),
          },
        ],
      },

      // Finance Store Payouts
      {
        path: '/finance-store-payouts',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceStorePayoutsPage />),
          },
        ],
      },

      // Pagos programados — liquidaciones automáticas a comercios e
      // influencers (scheduler + auditoría por corridas).
      {
        path: '/finance/scheduled-payouts',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ScheduledPayoutsPage />),
          },
        ],
      },

      // Finance Bank Accounts (review queue)
      // Acceso ampliado a roles de soporte/asesores — el backend filtra
      // las cuentas que cada uno puede ver (CompanyAdvisor scope).
      {
        path: '/finance-bank-accounts',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.BANK_ACCOUNT_REVIEW}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceBankAccountsPage />),
          },
        ],
      },

      // Finance Creator Commissions — redirects to unified creator finance page
      {
        path: '/finance-creator-commissions',
        element: <Navigate to="/finance-influencer-payouts" replace />,
      },

      // Finance Influencer Payouts — admin de cuentas bancarias,
      // solicitudes de retiro y batches de pago a creadores.
      {
        path: '/finance-influencer-payouts',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceInfluencerPayoutsPage />),
          },
        ],
      },

      // Finance Subscriptions & Invoicing
      {
        path: '/finance-subscriptions',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceSubscriptionsPage />),
          },
        ],
      },

      // Devoluciones de saldo 121 a cuenta bancaria
      {
        path: '/finance-wallet-withdrawals',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceWalletWithdrawalsPage />),
          },
        ],
      },

      // Finance Wallets
      {
        path: '/finance-wallets',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceWalletsPage />),
          },
        ],
      },

      // Finance Reports
      {
        path: '/finance-reports',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.FINANCE}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<FinanceReportsPage />),
          },
        ],
      },

      // =========================================================================
      // MARKETING ROUTES
      // =========================================================================

      // Regalos — bonos en dinero y Puntos 121, con las mismas pestañas
      // (Resumen · Otorgar · Campañas · Historial). El tipo y la pestaña van
      // en la ruta para que enlaces y guías lleguen a la pestaña exacta.
      {
        path: '/gifts',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/gifts/bonos/resumen" replace />,
          },
          {
            path: 'bonos/campanas/:id',
            element: withSuspense(<PromoCampaignDetailPage />),
          },
          {
            path: 'puntos/campanas/:id',
            element: withSuspense(<PointsCampaignDetailPage />),
          },
          {
            path: ':tipo',
            element: withSuspense(<GiftsPage />),
          },
          {
            path: ':tipo/:pestana',
            element: withSuspense(<GiftsPage />),
          },
        ],
      },

      // Rutas viejas de lo que hoy es «Regalos» (2026-10-15): enlaces
      // guardados y notificaciones antiguas siguen llegando.
      {
        path: '/campaigns',
        element: <Navigate to="/gifts/bonos/campanas" replace />,
      },
      {
        path: '/campaigns/:id',
        element: <LegacyCampaignRedirect />,
      },
      {
        path: '/promo-credits',
        element: <Navigate to="/gifts/bonos/resumen" replace />,
      },
      {
        path: '/points-flow',
        element: <Navigate to="/gifts/puntos/otorgar" replace />,
      },

      // Advertising (placeholder)
      {
        path: '/advertising',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<AdvertisingPage />),
          },
        ],
      },

      // Avisos (diálogos de ingreso)
      {
        path: '/in-app-notices',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<InAppNoticesPage />),
          },
        ],
      },

      // Statistics (placeholder)
      {
        path: '/statistics',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<StatisticsPage />),
          },
        ],
      },

      // Informe de referidos entre clientes. Mismos roles que el resto de lo
      // que mueve dinero: lo lee quien responde por el cuadre o por el fraude.
      {
        path: '/referral-report',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={['SUPER_ADMIN', 'FINANCE', 'SUPPORT_LEAD']}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<ReferralReportPage />),
          },
        ],
      },

      // Points & Cashback
      {
        path: '/points-cashback',
        element: (
          <ProtectedRoute requireAuth={true} allowedRoles={ROLES.MARKETING}>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<PointsCashbackPage />),
          },
        ],
      },

      // =========================================================================
      // COMPANY MODE — Entry point (auto-selects first store)
      // =========================================================================
      {
        path: '/my-companies/:companyId/manage',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.STORE_MANAGEMENT}
          >
            <CompanyModeRedirect />
          </ProtectedRoute>
        ),
      },

      // =========================================================================
      // STORE MANAGEMENT — Company Mode (advisor proxy routes)
      // =========================================================================
      {
        path: '/my-companies/:companyId/stores/:storeId/manage',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.STORE_MANAGEMENT}
          >
            <CompanyManagementLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="menu" replace />,
          },
          {
            path: 'menu',
            element: withSuspense(<StoreMenuPage />),
          },
          {
            path: 'catalog',
            element: withSuspense(<CatalogPage />),
          },
          {
            path: 'schedule',
            element: withSuspense(<StoreSchedulePage />),
          },
          {
            path: 'settings',
            element: withSuspense(<StoreSettingsPage />),
          },
          {
            path: 'promotions',
            element: withSuspense(<StorePromotionsPage />),
          },
          {
            path: 'loyalty',
            element: withSuspense(<StoreLoyaltyPage />),
          },
        ],
      },

      // My Companies — Create Store (advisor view)
      {
        path: '/my-companies/:companyId/stores/create',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.STORE_MANAGEMENT}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CreateStorePage />),
          },
        ],
      },

      // My Companies — Company Stores (advisor view)
      {
        path: '/my-companies/:companyId/stores',
        element: (
          <ProtectedRoute
            requireAuth={true}
            allowedRoles={ROLES.STORE_MANAGEMENT}
          >
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: withSuspense(<CompanyStoresPage />),
          },
        ],
      },

      // Legacy dashboard route (redirect to role-based default)
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute requireAuth={true}>
            <RoleBasedRedirect />
          </ProtectedRoute>
        ),
      },

      // =========================================================================
      // 404 - Not Found
      // =========================================================================
      {
        path: '*',
        element: withSuspense(<NotFoundPage />),
      },
    ],
  },
];

export const router = createBrowserRouter(routes);

export default router;
